import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ContactInquiry = ({ player }) => {
  const [formData, setFormData] = useState({
    clubName: '',
    contactName: '',
    email: '',
    phone: '',
    inquiryType: 'transfer',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!formData?.clubName || !formData?.contactName || !formData?.email || !formData?.message) {
      alert('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setSubmitted(true);
      setFormData({
        clubName: '',
        contactName: '',
        email: '',
        phone: '',
        inquiryType: 'transfer',
        message: ''
      });
      
      setTimeout(() => setSubmitted(false), 5000);
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      alert('Failed to submit inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-12">
      <div className="bg-gradient-to-br from-primary/10 via-background to-accent/10 border border-border rounded-2xl p-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Mail" size={32} className="text-primary-foreground" />
            </div>
            <h2 className="text-3xl font-bold text-foreground font-headline mb-2">Contact Player Representative</h2>
            <p className="text-muted-foreground">Interested in {player?.fullName}? Submit your inquiry below and we'll connect you with the player's representative.</p>
          </div>
          
          {player?.agentName && (
            <div className="bg-card border border-border rounded-lg p-4 mb-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="User" size={24} className="text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground mb-1">Player Representative</p>
                  <p className="font-bold text-foreground mb-2">{player?.agentName}</p>
                  {player?.agencyName && (
                    <p className="text-sm text-muted-foreground mb-2">{player?.agencyName}</p>
                  )}
                  <div className="flex flex-wrap gap-3 text-sm">
                    {player?.agentEmail && (
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Icon name="Mail" size={14} />
                        <span>{player?.agentEmail}</span>
                      </div>
                    )}
                    {player?.agentPhone && (
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Icon name="Phone" size={14} />
                        <span>{player?.agentPhone}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {submitted ? (
            <div className="bg-success/10 border border-success rounded-lg p-8 text-center">
              <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-4" />
              <h3 className="text-xl font-bold text-success mb-2">Inquiry Submitted Successfully!</h3>
              <p className="text-muted-foreground">The player's representative will review your inquiry and contact you shortly.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Club Name <span className="text-destructive">*</span>
                  </label>
                  <Input
                    type="text"
                    name="clubName"
                    value={formData?.clubName}
                    onChange={handleChange}
                    placeholder="Enter your club name"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Contact Name <span className="text-destructive">*</span>
                  </label>
                  <Input
                    type="text"
                    name="contactName"
                    value={formData?.contactName}
                    onChange={handleChange}
                    placeholder="Your full name"
                    required
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Email Address <span className="text-destructive">*</span>
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData?.email}
                    onChange={handleChange}
                    placeholder="your.email@club.com"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Phone Number
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData?.phone}
                    onChange={handleChange}
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Inquiry Type <span className="text-destructive">*</span>
                </label>
                <select
                  name="inquiryType"
                  value={formData?.inquiryType}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                >
                  <option value="transfer">Permanent Transfer</option>
                  <option value="loan">Loan Inquiry</option>
                  <option value="trial">Trial Request</option>
                  <option value="general">General Inquiry</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Message <span className="text-destructive">*</span>
                </label>
                <textarea
                  name="message"
                  value={formData?.message}
                  onChange={handleChange}
                  rows={6}
                  placeholder="Please provide details about your inquiry, including budget, contract terms, and timeline..."
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  required
                />
              </div>
              
              <Button
                type="submit"
                disabled={submitting}
                className="w-full py-3 text-lg"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-foreground"></div>
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Icon name="Send" size={20} />
                    Submit Inquiry
                  </span>
                )}
              </Button>
              
              <p className="text-xs text-muted-foreground text-center">
                By submitting this form, you agree to our terms of service and privacy policy. All inquiries are confidential and will be handled professionally.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default ContactInquiry;